
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../../home/components/Navigation';

const UGCContentPage = () => {
  const features = [
    {
      icon: 'ri-user-star-line',
      title: 'Authentic Creator Content',
      description: 'Work with real users to create genuine, relatable content that builds trust.'
    },
    {
      icon: 'ri-camera-line',
      title: 'Multi-Platform Formats',
      description: 'Content optimized for Instagram, TikTok, YouTube, and other social platforms.'
    },
    {
      icon: 'ri-speed-up-line',
      title: 'Fast Turnaround',
      description: 'Get your UGC content delivered quickly without compromising quality.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Cost-Effective',
      description: 'More affordable than traditional advertising with higher engagement rates.'
    },
    {
      icon: 'ri-heart-line',
      title: 'High Engagement',
      description: 'UGC generates 28% higher engagement rates than brand-created content.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Brand Safe',
      description: 'All content is reviewed and approved to maintain your brand standards.'
    }
  ];

  const useCases = [
    {
      title: 'Product Reviews',
      description: 'Honest, detailed reviews from real customers showcasing your products in action.',
      image: 'https://public.readdy.ai/ai/img_res/e226b055c7e1d5b384a193c4fc07d645.jpg'
    },
    {
      title: 'Tutorial Content',
      description: 'Step-by-step tutorials showing how to use your products effectively.',
      image: 'https://readdy.ai/api/search-image?query=Person%20creating%20tutorial%20video%20content%20showing%20product%20usage%2C%20hot%20pink%20and%20aqua%20branded%20setup%2C%20authentic%20teaching%20moment%2C%20professional%20but%20approachable%20atmosphere%2C%20high%20quality%20social%20media%20content%20style&width=600&height=400&seq=ugc-tutorial-1&orientation=landscape'
    },
    {
      title: 'Lifestyle Integration',
      description: 'Natural integration of your products into real customer lifestyles.',
      image: 'https://readdy.ai/api/search-image?query=Lifestyle%20content%20showing%20product%20naturally%20integrated%20into%20daily%20routine%2C%20hot%20pink%20and%20aqua%20color%20scheme%2C%20authentic%20user-generated%20style%2C%20real%20person%20in%20genuine%20environment%2C%20high%20quality%20social%20content&width=600&height=400&seq=ugc-lifestyle-1&orientation=landscape'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Brief & Strategy',
      description: 'We understand your brand goals and create a content strategy with creator guidelines.'
    },
    {
      step: '02',
      title: 'Creator Matching',
      description: 'We connect you with vetted creators who align with your brand values and audience.'
    },
    {
      step: '03',
      title: 'Content Creation',
      description: 'Creators produce authentic content following your brand guidelines and requirements.'
    },
    {
      step: '04',
      title: 'Review & Delivery',
      description: 'Content is reviewed, refined if needed, and delivered in your preferred formats.'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-40 pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=User-generated%20content%20creation%20scene%20with%20diverse%20creators%20filming%20authentic%20content%2C%20hot%20pink%20and%20aqua%20neon%20lighting%20effects%2C%20modern%20social%20media%20studio%20setup%2C%20multiple%20phones%20and%20cameras%2C%20vibrant%20creative%20atmosphere%2C%20high%20quality%20photography&width=1920&height=1080&seq=ugc-hero-1&orientation=landscape"
            alt="UGC Content Creation"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-brand-pink/20 to-brand-aqua/20 backdrop-blur-xl border border-brand-pink/30 mb-8">
              <i className="ri-user-heart-line text-brand-pink text-xl"></i>
              <span className="text-sm font-semibold text-white">Authentic Creator Content</span>
            </div>
            
            <h1 className="text-7xl font-light mb-8">
              <span className="text-white">UGC </span>
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Content</span>
            </h1>
            
            <p className="text-2xl text-white/80 max-w-4xl mx-auto mb-12 leading-relaxed">
              Harness the power of authentic user-generated content to build trust, 
              increase engagement, and drive conversions through real customer stories.
            </p>
            
            <div className="flex items-center justify-center gap-6">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-10 py-5 bg-gradient-to-r from-brand-pink to-brand-aqua rounded-full font-bold text-lg whitespace-nowrap cursor-pointer"
                >
                  Start Your UGC Campaign
                </motion.button>
              </Link>
              
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-10 py-5 border-2 border-white/20 rounded-full font-semibold text-lg whitespace-nowrap cursor-pointer hover:border-brand-pink transition-colors"
                >
                  View All Services
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl font-light mb-6">
              <span className="text-white">Why Choose </span>
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">UGC Content?</span>
            </h2>
            <p className="text-xl text-white/60 max-w-3xl mx-auto">
              User-generated content builds authentic connections and drives real results for your brand.
            </p>
          </motion.div>

          <div className="grid grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="p-8 rounded-2xl bg-gradient-to-b from-white/5 to-white/2 border border-white/10 backdrop-blur-xl"
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-r from-brand-pink/20 to-brand-aqua/20 mb-6">
                  <i className={`${feature.icon} text-3xl bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent`}></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">{feature.title}</h3>
                <p className="text-white/70 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl font-light mb-6">
              <span className="text-white">Content </span>
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Types</span>
            </h2>
          </motion.div>

          <div className="space-y-24">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`grid ${index % 2 === 0 ? 'grid-cols-[1fr,1.2fr]' : 'grid-cols-[1.2fr,1fr]'} gap-16 items-center`}
              >
                <div className={`${index % 2 === 1 ? 'order-2' : ''}`}>
                  <h3 className="text-4xl font-light text-white mb-6">{useCase.title}</h3>
                  <p className="text-xl text-white/70 leading-relaxed">{useCase.description}</p>
                </div>
                
                <div className={`${index % 2 === 1 ? 'order-1' : ''}`}>
                  <div className="relative rounded-2xl overflow-hidden">
                    <img
                      src={useCase.image}
                      alt={useCase.title}
                      className="w-full h-80 object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl font-light mb-6">
              <span className="text-white">Our </span>
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Process</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-4 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative text-center"
              >
                <div className="relative mb-8">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-r from-brand-pink to-brand-aqua mx-auto mb-4 text-2xl font-bold text-white">
                    {item.step}
                  </div>
                  {index < process.length - 1 && (
                    <div className="absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-brand-pink to-brand-aqua hidden xl:block"></div>
                  )}
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">{item.title}</h3>
                <p className="text-white/70">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand-pink/10 to-brand-aqua/10 border border-brand-pink/20 p-20 text-center"
          >
            <h2 className="text-5xl font-light text-white mb-6">
              Ready to Launch Your UGC Campaign?
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
              Connect with authentic creators and build trust through genuine user-generated content.
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-12 py-5 bg-gradient-to-r from-brand-pink to-brand-aqua rounded-full text-white font-bold text-lg whitespace-nowrap cursor-pointer"
              >
                Get Started Today
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default UGCContentPage;